import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { setSentryUser, addSentryBreadcrumb } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let isMounted = true;
    
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);
          
          // Update Sentry user context
          if (session?.user) {
            setSentryUser({
              id: session.user.id,
              email: session.user.email,
              username: session.user.user_metadata?.username,
            });
            addSentryBreadcrumb(`User ${event}`, 'auth', { userId: session.user.id });
          } else {
            setSentryUser({ id: '', email: '', username: '' });
            addSentryBreadcrumb('User signed out', 'auth');
          }
          
          setLoading(false);
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted) {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Set Sentry user context
        if (session?.user) {
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
            username: session.user.user_metadata?.username,
          });
          addSentryBreadcrumb('User session restored', 'auth');
        }
        
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, userData?: any) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: userData
      }
    });
    
    if (error) {
      toast({
        title: "Signup Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Success",
        description: "Account created successfully! You can now continue to set up your profile."
      });
    }
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    addSentryBreadcrumb('Sign in attempt', 'auth', { email });
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      addSentryBreadcrumb('Sign in failed', 'auth', { error: error.message });
      toast({
        title: "Login Error",
        description: error.message,
        variant: "destructive"
      });
    } else {
      addSentryBreadcrumb('Sign in successful', 'auth', { email });
    }
    
    return { error };
  };

  const signOut = async () => {
    addSentryBreadcrumb('Sign out initiated', 'auth');
    
    try {
      // Try to sign out globally first
      const { error } = await supabase.auth.signOut();
      
      // If there's a session error, try local signout
      if (error && error.message?.includes('session')) {
        await supabase.auth.signOut({ scope: 'local' });
      } else if (error) {
        // For other errors, still clear local state but show warning
        console.warn('Signout warning:', error.message);
        addSentryBreadcrumb('Sign out warning', 'auth', { error: error.message });
        await supabase.auth.signOut({ scope: 'local' });
      }
    } catch (error) {
      // If everything fails, clear local state anyway
      console.error('Signout error, clearing local session:', error);
      addSentryBreadcrumb('Sign out error', 'auth', { error: error.toString() });
      await supabase.auth.signOut({ scope: 'local' });
    }
    
    // Clear local state immediately regardless of server response
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};